import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { listOrders } from '../../services/liveStamping';
import { formatISTDateOnly } from '../../utils/dateUtils';

const STATUS_LABELS = {
  executing: 'Awaiting preview upload',
  preview_submitted: 'Preview under admin review',
  metadata_ready: 'Awaiting final doc upload',
  compliance_check: 'Final doc under admin review',
  pending_verification: 'Under review',
  completed: 'Done',
  rejected: 'Rejected',
  placed: 'Placed',
  payment_pending: 'Payment pending',
};

const STATUS_COLORS = {
  executing: 'bg-orange-100 text-orange-800',
  preview_submitted: 'bg-violet-100 text-violet-800',
  metadata_ready: 'bg-teal-100 text-teal-800',
  compliance_check: 'bg-purple-100 text-purple-800',
  pending_verification: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  placed: 'bg-blue-100 text-blue-800',
  payment_pending: 'bg-pink-100 text-pink-800',
};

const LIMIT = 15;

const VendorQueue = forwardRef(({ onSelectOrder }, ref) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchOrders = useCallback(async (p = 1, status = statusFilter) => {
    setLoading(true);
    try {
      const filters = { limit: LIMIT, offset: (p - 1) * LIMIT };
      if (status) filters.status = status;
      const res = await listOrders(filters);
      setOrders(res.orders || []);
      setTotal(res.total || 0);
      setPage(p);
    } catch (err) {
      console.error('VendorQueue fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchOrders(1, statusFilter); }, [statusFilter]); // eslint-disable-line

  useImperativeHandle(ref, () => ({ refresh: () => fetchOrders(page, statusFilter) }));

  const totalPages = Math.ceil(total / LIMIT) || 1;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Filter bar */}
      <div className="p-4 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-3 items-center justify-between">
        <h3 className="font-bold text-gray-800">Vendor Queue</h3>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Status</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
          >
            <option value="">All</option>
            {Object.entries(STATUS_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
          <span className="text-sm text-gray-500 ml-2">{total} order{total !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Order ID</th>
              <th className="px-4 py-3 text-left font-semibold">Service</th>
              <th className="px-4 py-3 text-left font-semibold">Client</th>
              <th className="px-4 py-3 text-left font-semibold">Amount</th>
              <th className="px-4 py-3 text-left font-semibold">Stage</th>
              <th className="px-4 py-3 text-left font-semibold">Created</th>
              <th className="px-4 py-3 text-center font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 py-10 text-center text-gray-400">
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    Loading…
                  </div>
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-10 text-center text-gray-400 italic">No orders found</td>
              </tr>
            ) : orders.map(order => (
              <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-sm font-medium text-blue-600">#{order.id}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{order.service_name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{order.client_name || '—'}</td>
                <td className="px-4 py-3 text-sm font-medium text-gray-800">₹{order.amount}</td>
                <td className="px-4 py-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-700'}`}>
                    {STATUS_LABELS[order.status] || order.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {formatISTDateOnly(order.created_at)}
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => onSelectOrder(order)}
                    className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
        <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <button
            onClick={() => fetchOrders(page - 1)}
            disabled={page <= 1}
            className="px-3 py-1 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => fetchOrders(page + 1)}
            disabled={page >= totalPages}
            className="px-3 py-1 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
});

VendorQueue.displayName = 'VendorQueue';
export default VendorQueue;
